import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import RichTextEditor from "../Common/RichTextEditor";

interface BulkTasksProps {
  onTasksChange?: (tasks: string[]) => void;
}

const extractLinesFromHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const lines: string[] = [];

  const pushLine = (value: string) => {
    const text = value.replace(/\u00A0/g, " ").trim();
    if (text) {
      // Split by any internal newlines (e.g., from replaced <br> elements)
      text.split(/\r?\n/).forEach((line) => {
        const trimmed = line.trim();
        if (trimmed) lines.push(trimmed);
      });
    }
  };

  // Replace all <br> elements with a newline text node
  doc.querySelectorAll("br").forEach((br) => {
    br.replaceWith(doc.createTextNode("\n"));
  });

  // Iterate through all childNodes (supports both text nodes and element nodes)
  Array.from(doc.body.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushLine(node.textContent ?? "");
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const tagName = element.tagName.toLowerCase();

      if (tagName === "ul" || tagName === "ol") {
        Array.from(element.querySelectorAll("li")).forEach((item) => {
          pushLine(item.textContent ?? "");
        });
      } else {
        pushLine(element.textContent ?? "");
      }
    }
  });

  return lines;
};

const BulkTasks: React.FC<BulkTasksProps> = ({ onTasksChange }) => {
  const [bulkText, setBulkText] = useState("");
  const [isExpanded, setIsExpanded] = useState(true);

  const generatedTasks = useMemo(() => {
    return bulkText
      .split(/\r?\n/)
      .map(task => task.trim())
      .filter(task => task?.length > 0);
  }, [bulkText]);

  useEffect(() => {
    onTasksChange?.(generatedTasks);
  }, [generatedTasks, onTasksChange]);

  useEffect(() => {
    return () => {
      onTasksChange?.([]);
    };
  }, [onTasksChange]);

  return (
    <Box>
        <Box className="bulk-task-container">
            <Box className="bulk-task-field-wrap">
              <Typography component="label" className="bulk-task-label">
                Bulk Task <span className="bulk-task-required">*</span>
              </Typography>
            <Box
              sx={{
                "& .rich-text-editor .ProseMirror": {
                  minHeight: "290px",
                  padding: "12px 16px 16px",
                  lineHeight: "28px",
                },
                "& .rich-text-editor .ProseMirror p": {
                  margin: "0 0 10px",
                },
                "& .rich-text-editor .ProseMirror p:last-child": {
                  marginBottom: 0,
                },
              }}
            >
              <RichTextEditor 
                placeholder={"Type in or paste the task titles here to create multiple tasks out of it"}
                hideMenu 
                autoFocus
                hidePretext
                height={"310px"}
                parentPage="action_items_bulk"
                onContentUpdate={(content) => {
                  setBulkText(extractLinesFromHtml(content).join("\n"));
                }}
              />
            </Box>
            </Box>
            <Box className="bulk-task-footer">
                <Box className="bulk-info-text-box">
                    <InfoOutlinedIcon sx={{color:"#6036D6", width: 20, hieght: 20}}/>
                    <Typography className="bulk-task-hint">
                        For Perfectly creating Separate tasks, have a line breaking in between.
                    </Typography>
                </Box>
            </Box>
        </Box>

      <Box mt={2}>
        <Box
            className="generated-tasks-header"
            onClick={() => setIsExpanded((prev) => !prev)}
            >
            <Typography className="generated-tasks-text">
                Generated Tasks ({generatedTasks.length})
            </Typography>

            {isExpanded ? (
                <ExpandLessOutlinedIcon />
            ) : (
                <ExpandMoreOutlinedIcon />
            )}
        </Box>
        {isExpanded && (
            <Box className="generated-tasks-preview">
                {generatedTasks?.length === 0 ? (
                <Typography
                    sx={{
                    px: 2,
                    py: 3,
                    color: "#98A2B3",
                    fontSize: "14px",
                    }}
                >
                    Preview here...
                </Typography>
                ) : (
                <List disablePadding>
                    {generatedTasks.map((task, index) => (
                    <ListItem
                        key={index}
                        sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                        px: 1,
                        py: 1,
                        }}
                    >
                        <TaskAltIcon
                        sx={{
                            width: 18,
                            height: 18,
                            mt: "2px",
                            flexShrink: 0,
                        }}
                        />
                        <Typography
                        sx={{
                            fontSize: "14px",
                            lineHeight: "20px",
                            wordBreak: "break-word",
                        }}
                        >
                        {task}
                        </Typography>
                    </ListItem>
                    ))}
                </List>
                )}
            </Box>
        )}
      </Box>
    </Box>
  );
};

export default BulkTasks;
